"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import Link from "next/link";
import Image from '@/components/ui/Image';
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";

interface NotFoundProps {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string; 
    image?: string;
    imageAltText?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const NotFound = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: NotFoundProps) => {

  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="notFound"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Not Found"
    > 
      <div className="container"> 
        <div className="flex flex-col gap-6 md:gap-16">
          <div className="flex flex-col gap-4">
            {contents?.badge && (
              <Badge variant="secondary" className="text-muted-foreground paragraph-lg pl-0">
                {contents?.badge}
              </Badge>
            )}
            {contents?.title && (
              <h1>
                {parse(contents.title)}
              </h1>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-20 lg:gap-24">
            <div className="w-full lg:w-12/24 flex">
              <div className="flex flex-col gap-6">
                
                {contents?.content && (
                  <div className="mt-4 paragraph-lg content-accordion">
                    {parse(contents?.content)}
                  </div>
                )}
                {contents?.hasButton && (
                  <div className="mt-2">
                    <div className="flex flex-col items-center gap-4 lg:flex-row">
                      <Button
                        asChild
                        size='lg'
                        className="w-full sm:w-auto"
                        variant={contents?.buttonStyle || "primary"}
                      >
                        <Link href={contents?.buttonLink ?? ""}>{contents?.buttonText}</Link>
                      </Button>
                      {contents?.secondaryButton && (
                        <Button
                          className="w-full sm:w-auto"
                          variant={contents?.secondaryButtonStyle || "outline"}
                          asChild
                          size='lg'
                        >
                          <Link href={contents?.secondaryButtonLink ?? ""}> 
                            {contents?.secondaryButtonText}
                          </Link>
                        </Button>
                      )}
                    </div>
                    {contents?.microCopy && (
                      <p className="mt-2 paragraph-sm text-muted-foreground">
                        {contents?.microCopy}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="w-full lg:w-12/24 flex items-center justify-center rounded-xl sm:rounded-2xl xl:rounded-3xl">
              {contents?.image && (
                <Image
                  src={contents?.image}
                  alt={contents?.imageAltText || ''}
                  title={contents?.imageAltText || ''}
                  width={688}
                  height={521}
                  className="object-cover md:max-w-[70%] rounded-2xl"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { NotFound };
